import { Alert, Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CustomTypography } from "./CustomTypography";
import { formatChipBgColorAvaliability, formatChipFontColorAvaliability, formatDateRange, formatDateTimeForDBQuery, formatNumberWithCommas, getImagePath, getTotalRow, downloadCSV, downloadComparisonCSV } from "utils/AppUtil";
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import BarGraph from "views/BarGraph";
import { ComparisonCarrierTable } from "./ComparisonCarrierTable";
import { ColorDotPicker } from "./ColorDotPicker";
import { DEFAULT_COMPARISON_COLORS } from "utils/colorOptions";
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';

import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import TableViewIcon from '@mui/icons-material/TableView';
import { baseUrl } from "api/baseQuery";
import { isSingleDayRange } from "utils/dateRange";




const CarrierColors = {
    "AT&T": "#00A8E1",
    "Juice": "#FF8C00",
    "Private": "#FF8C00",
    "T-Mobile": "#E20074",
};




// Styled Components
const UniversityImage = styled("img")({
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    width: { xs: "0rem", sm: "0rem", md: "15rem" },
    height: "4rem",
    display: { xs: "none !important", sm: "none !important", md: "block !important" }
});



const CarrierImage = styled("img")({
    maxWidth: "8rem",
    maxHeight: "100%",
    objectFit: "contain",
    height: "2.5rem"
});




const TableContainer = styled(Box)({
    width: "100%",
});




const TableWrapper = styled(Box)(({ theme }) => ({
    borderRadius: "4px",
}));

// 3. TableInnerWrapper – make sure it doesn't add extra width
const TableInnerWrapper = styled(Box)(({ theme }) => ({
    width: "100%",
    [theme.breakpoints.down("sm")]: {
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": {
            height: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
        },
    },
}));
// 1. TableHeaderWrapper – remove extra width tricks, keep it simple
const TableHeaderWrapper = styled(Box)(({ theme }) => ({
    borderRadius: "6px",
    backgroundColor: "#0A2240",
    color: "white",
    marginBottom: "10px",
    padding: "8px 8px", // same as rows

    [theme.breakpoints.down("sm")]: {
        minWidth: "800px",        // same as CarrierRow & TotalRow
        width: "800px",           // <-- THIS IS THE FIX: force exact same width
        padding: "8px 6px",
    },
}));

// 2. TableHeaderRow – exact same grid + width as carrier rows
const TableHeaderRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "minmax(150px, 1fr) repeat(7, 1fr)",
    alignItems: "center",
    gap: "8px",
    width: "100%",

    [theme.breakpoints.down("sm")]: {
        minWidth: "800px",
        width: "800px",           // <-- force exact width
        gap: "6px",
        padding: "0 6px",
        fontSize: "0.825rem",
    },
}));
const HeaderCell = styled(Box)(({ colspan, borderLeft }) => ({
    gridColumn: colspan ? `span ${colspan}` : "span 1",
    textAlign: "left",
    borderLeft: borderLeft ? "2px solid #fff" : "none",
    paddingLeft: borderLeft ? "12px" : "0",
}));



// 1. CarrierRow – make it scrollable horizontally on mobile + better column handling
const CarrierRow = styled(Box)(({ carrierName, theme }) => ({
    display: "grid",
    gridTemplateColumns: "minmax(150px, 1fr) repeat(7, 1fr)",   // flexible columns
    backgroundColor: "white",
    borderRadius: "14px",
    border: "1px solid #0000001F",
    padding: "12px 8px",                                      // reduced horizontal padding
    alignItems: "center",
    borderLeft: `4px solid ${CarrierColors[carrierName] || "#000"}`,
    marginBottom: "8px",
    gap: "8px",                                               // consistent spacing

    // Mobile fixes
    [theme.breakpoints.down("sm")]: {
        minWidth: "800px",                 // forces horizontal scroll when needed
        fontSize: "0.825rem",
        padding: "10px 6px",
        gap: "6px",
    },

    // Pseudo-elements for the vertical divider lines stay the same
    "& > *:nth-of-type(2)": { borderLeft: "2px solid #0000001F", paddingLeft: "8px" },
    "& > *:nth-of-type(5)": { borderLeft: "2px solid #0000001F", paddingLeft: "8px" },
    "& > *:nth-of-type(8)": { borderLeft: "2px solid #0000001F", paddingLeft: "8px" },
}));

// 2. TotalRow – same treatment as CarrierRow
const TotalRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "minmax(150px, 1fr) repeat(7, 1fr)",
    backgroundColor: "#F5F5F5",
    borderTop: "6px solid #2c3e50",
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    padding: "12px 8px",
    alignItems: "center",
    marginBottom: "8px",
    gap: "8px",

    [theme.breakpoints.down("sm")]: {
        minWidth: "800px",
        fontSize: "0.825rem",
        padding: "10px 6px",
        gap: "6px",
    },
}));




const DataUnitSpan = styled("span")({
    fontSize: "12px",
    fontWeight: 400,
    marginLeft: "5px"
});




const CarrierImageContainer = styled(Box)({
    display: "flex",
    alignItems: "left",
    gap: "8px",
});


const CellContainer = styled(Box)(({ borderLeft }) => ({
    textAlign: "left",
    borderLeft: borderLeft ? "2px solid #0000001F" : "none",
    paddingLeft: borderLeft ? "8px" : "0",
}));



const TotalRowCellContainer = styled(Box)(({ borderLeft }) => ({
    textAlign: "left",
    borderLeft: borderLeft ? "2px solid #0000001F" : "none",
    paddingLeft: borderLeft ? "8px" : "0",
}));




const TodayCountContainer = styled(Box)({
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    gap: "4px",
});




const DataUsageContainer = styled(Box)({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "left",
});




export const CarrierTable = ({ universityName, carriers, tableData, dateSelectionMode, sitePerf, onViewChange, selectedDates: selectedDatesProp, volteCallsCount: volteCallsCountProp, currentView: controlledView, comparisonDateRanges, comparisonTableData, comparisonVolteCallsCount, isLoadingComparisonData, comparisonDataError }) => {
    const { selectedDates: storedSelectedDates, volteCallsCount: storedVolteCallsCount } = useSelector((state) => state.home);
    const selectedDates = selectedDatesProp ?? storedSelectedDates;
    const volteCallsCount = volteCallsCountProp ?? storedVolteCallsCount;
    const perfValue = sitePerf?.[universityName] ?? '';
    const [comparisonColors, setComparisonColors] = useState(DEFAULT_COMPARISON_COLORS);
    const handleComparisonColorChange = (key, hex) => setComparisonColors((prev) => ({ ...prev, [key]: hex }));

    // View state management
    const [internalView, setInternalView] = useState('table');
    const currentView = controlledView ?? internalView;
    const [downloadStatus, setDownloadStatus] = useState('idle'); // 'idle', 'downloading', 'success', 'error'
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const anchorRef = useRef(null);
    const totals = getTotalRow(tableData);
    const graphDateRanges = comparisonDateRanges?.first && comparisonDateRanges?.second
        ? [comparisonDateRanges.first, comparisonDateRanges.second]
        : [selectedDates];
    const graphAvailable = graphDateRanges.every((dates) => !isSingleDayRange(dates));

    const viewOptions = [
        { value: 'table', label: 'Table', icon: <TableViewIcon sx={{ fontSize: "18px", mr: 1 }} /> },
        ...(graphAvailable
            ? [{ value: 'chart', label: 'Graph', icon: <InsertChartIcon sx={{ fontSize: "18px", mr: 1 }} /> }]
            : []),
        { value: 'download', label: 'Download', icon: <DownloadForOfflineIcon sx={{ fontSize: "18px", mr: 1 }} /> }
    ];
    const effectiveView = currentView === 'chart' && !graphAvailable ? 'table' : currentView;
    const activeSelectedIndex = Math.max(0, viewOptions.findIndex((option) => option.value === effectiveView));

    const updateCurrentView = (view) => {
        if (controlledView === undefined) {
            setInternalView(view);
        }
        if (onViewChange) {
            onViewChange(view, tableData);
        }
    };

    useEffect(() => {
        if (graphAvailable || currentView !== 'chart') return;

        if (controlledView === undefined) {
            setInternalView('table');
        }
        if (onViewChange) {
            onViewChange('table', tableData);
        }
    }, [graphAvailable, currentView, controlledView, onViewChange, tableData]);

    const handleMenuItemClick = (event, index) => {
        const selectedOption = viewOptions[index].value;

        setDropdownOpen(false);

        if (selectedOption === 'table') {
            handleTableClick();
        } else if (selectedOption === 'chart') {
            handleChartClick();
        } else if (selectedOption === 'download') {
            handleDownloadClick();
        }
    };

    const handleDropdownToggle = () => {
        setDropdownOpen((prevOpen) => !prevOpen);
    };

    const handleDropdownClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setDropdownOpen(false);
    };
    // Handle table button click
    const handleTableClick = () => {
        updateCurrentView('table');
        setDownloadStatus('idle');
    };

    // Handle chart button click
    const handleChartClick = () => {
        updateCurrentView('chart');
        setDownloadStatus('idle');
    };

    // Handle download button click
    const handleDownloadClick = async () => {
        // setCurrentView('download');
        setDownloadStatus('downloading');
        const selectedSite = { syracyse: 0, caesars: 1, jmahq: 2 }[universityName] ?? 0;
        const site = { syracyse: 'su', caesars: 'caesars', jmahq: '5g' }[universityName] ?? 'su';
        const dbDates = formatDateTimeForDBQuery(selectedDates, selectedSite);
        try {
            const sURL = baseUrl + `/getCSV/?site=${site}&start_date=${dbDates.start}&end_date=${dbDates.end}`;
            if (comparisonDateRanges?.first && comparisonDateRanges?.second) {
                const firstDbDates = formatDateTimeForDBQuery(comparisonDateRanges.first, selectedSite);
                const secondDbDates = formatDateTimeForDBQuery(comparisonDateRanges.second, selectedSite);
                await downloadComparisonCSV({
                    firstUrl: baseUrl + `/getCSV/?site=${site}&start_date=${firstDbDates.start}&end_date=${firstDbDates.end}`,
                    secondUrl: baseUrl + `/getCSV/?site=${site}&start_date=${secondDbDates.start}&end_date=${secondDbDates.end}`,
                    site,
                    firstDates: comparisonDateRanges.first,
                    secondDates: comparisonDateRanges.second,
                });
            } else {
                await downloadCSV(sURL, site, selectedDates);
            }
            setDownloadStatus('success');
        } catch (error) {
            setDownloadStatus('error');
        }
    };

    // Render based on current view  
    const renderView = () => {
        switch (effectiveView) {
            case 'chart':
                if (comparisonDateRanges?.first && comparisonDateRanges?.second) {
                    const graphRanges = [
                        { key: 'first', dates: comparisonDateRanges.first, color: comparisonColors.first },
                        { key: 'second', dates: comparisonDateRanges.second, color: comparisonColors.second, isComparison: true },
                    ];

                    return (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
                                gap: 3,
                                alignItems: 'start',
                            }}
                        >
                            {graphRanges.map(({ key, dates, color, isComparison }) => (
                                <Box key={formatDateRange(dates)} sx={{ minWidth: 0 }}>
                                    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ColorDotPicker color={color} onChange={(hex) => handleComparisonColorChange(key, hex)} disabledColor={key === 'first' ? comparisonColors.second : comparisonColors.first} />
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color }}>
                                            {formatDateRange(dates)}
                                        </Typography>
                                    </Box>
                                    {isComparison && isLoadingComparisonData ? (
                                        <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CircularProgress size={36} aria-label="Loading comparison data" />
                                        </Box>
                                    ) : isComparison && comparisonDataError ? (
                                        <Alert severity="error">Unable to load the comparison range.</Alert>
                                    ) : (
                                        <BarGraph universityName={universityName} selectedDates={dates} />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    );
                }

                return (
                    <BarGraph universityName={universityName} selectedDates={selectedDates} />
                );
            case 'download':
                if (downloadStatus === 'downloading') {
                    return (
                        <Box sx={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <CircularProgress size={40} />
                            <Typography>Download in progress...</Typography>
                        </Box>
                    );
                } else if (downloadStatus === 'success') {
                    return (
                        <Alert severity="success" sx={{ width: '100%' }}>
                            Download completed successfully!
                        </Alert>
                    );
                } else if (downloadStatus === 'error') {
                    return (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            Download failed. Please try again.
                        </Alert>
                    );
                }
                return null;
            case 'table':
            default:
                if (comparisonDateRanges?.first && comparisonDateRanges?.second) {
                    return (
                        <ComparisonCarrierTable
                            universityName={universityName}
                            firstData={tableData}
                            secondData={comparisonTableData}
                            firstDates={comparisonDateRanges.first}
                            secondDates={comparisonDateRanges.second}
                            firstVolteCallsCount={volteCallsCount}
                            secondVolteCallsCount={comparisonVolteCallsCount}
                            isLoading={isLoadingComparisonData}
                            hasError={comparisonDataError}
                            comparisonColors={comparisonColors}
                            onComparisonColorChange={handleComparisonColorChange}
                        />
                    );
                }
                return (
                    <>
                        <TableWrapper>
                            <TableInnerWrapper>
                                {/* Your existing table header code remains EXACTLY THE SAME */}
                                <TableHeaderWrapper>
                                    <TableHeaderRow>
                                        <HeaderCell>
                                            <CustomTypography text="Carrier" sx={{ fontWeight: 600, fontSize: "14px" }} />
                                        </HeaderCell>
                                        <HeaderCell colspan={3} borderLeft>
                                            <Stack>
                                                <CustomTypography
                                                    text="User Count"
                                                    sx={{ fontWeight: 700, fontSize: "13px", textAlign: "left", fontFamily: "Montserrat" }}
                                                />
                                                <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#ccc", marginTop: "4px" }}>
                                                    {formatDateRange(selectedDates).includes(" - ")
                                                        ? `Average per day from ${formatDateRange(selectedDates)}`
                                                        : formatDateRange(selectedDates)
                                                    }
                                                </Typography>
                                            </Stack>
                                        </HeaderCell>
                                        <HeaderCell colspan={2} borderLeft>
                                            <Stack>
                                                <CustomTypography text="Data Usage" sx={{ fontWeight: 600, fontSize: "13px", textAlign: "left" }} />
                                                <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#ccc", marginTop: "4px" }}>
                                                    {formatDateRange(selectedDates).includes(" - ")
                                                        ? `Total from ${formatDateRange(selectedDates)}`
                                                        : formatDateRange(selectedDates)
                                                    }
                                                </Typography>
                                            </Stack>
                                        </HeaderCell>
                                        <HeaderCell colspan={2} borderLeft>
                                            <Stack>
                                                <CustomTypography text="VoLTE" sx={{ fontWeight: 600, fontSize: "13px", textAlign: "left" }} />
                                                <Typography sx={{ fontSize: "13px", fontWeight: 400, color: "#ccc", marginTop: "4px" }}>
                                                    {formatDateRange(selectedDates).includes(" - ")
                                                        ? `Total from ${formatDateRange(selectedDates)}`
                                                        : formatDateRange(selectedDates)
                                                    }
                                                </Typography>
                                            </Stack>
                                        </HeaderCell>
                                    </TableHeaderRow>
                                </TableHeaderWrapper>

                                {/* Your existing carrier rows code remains EXACTLY THE SAME */}
                                {tableData?.map((carrier, index) => (
                                    <CarrierRow key={index} carrierName={carrier.name}>
                                        <CarrierImageContainer>
                                            <CarrierImage
                                                src={getImagePath(carrier.name, universityName)}
                                                alt={`Image for ${carrier.name}`}
                                            />
                                        </CarrierImageContainer>

                                        {dateSelectionMode === "today" && (
                                            <>
                                                <CellContainer>
                                                    <CustomTypography text={formatNumberWithCommas(carrier.yesterday)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                    <CustomTypography text="Yesterday" sx={{ fontSize: "11px", color: "#999" }} />
                                                </CellContainer>

                                                <CellContainer>
                                                    <TodayCountContainer>
                                                        <CustomTypography text={formatNumberWithCommas(carrier.userCount)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                    </TodayCountContainer>
                                                    <CustomTypography text="Today" sx={{ fontSize: "11px", color: "#999" }} />
                                                </CellContainer>

                                                <CellContainer>
                                                    <CustomTypography text={formatNumberWithCommas(carrier.live)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                    <CustomTypography text="Live" sx={{ fontSize: "11px", color: "#999" }} />
                                                </CellContainer>
                                            </>
                                        )}

                                        {dateSelectionMode === "range" && (
                                            <>
                                                <CellContainer sx={{ marginLeft: "-0.4rem" }}>
                                                    <CustomTypography text={formatNumberWithCommas(carrier.userCount)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                    <CustomTypography text="Users" sx={{ fontSize: "11px", color: "#999" }} />
                                                </CellContainer>
                                                <CellContainer>
                                                    <CustomTypography text={""} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                </CellContainer>
                                                <CellContainer>
                                                    <CustomTypography text={""} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                </CellContainer>
                                            </>
                                        )}

                                        <CellContainer>
                                            <DataUsageContainer>
                                                <CustomTypography text={carrier.download} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                <DataUnitSpan>GB</DataUnitSpan>
                                            </DataUsageContainer>
                                            <CustomTypography text="Download" sx={{ fontSize: "11px", color: "#999" }} />
                                        </CellContainer>

                                        <CellContainer>
                                            <DataUsageContainer>
                                                <CustomTypography text={carrier.upload} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                <DataUnitSpan>GB</DataUnitSpan>
                                            </DataUsageContainer>
                                            <CustomTypography text="Upload" sx={{ fontSize: "11px", color: "#999" }} />
                                        </CellContainer>

                                        <CellContainer borderLeft>
                                            <DataUsageContainer>
                                                <CustomTypography text={carrier.volte} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                            </DataUsageContainer>
                                            <CustomTypography text="Calls" sx={{ fontSize: "11px", color: "#999" }} />
                                        </CellContainer>
                                    </CarrierRow>
                                ))}

                                {/* Your existing total row code remains EXACTLY THE SAME */}
                                <TotalRow>
                                    <CustomTypography text="TOTAL:" sx={{ fontWeight: 700, fontSize: "14px" }} />

                                    {dateSelectionMode === "today" && (
                                        <>
                                            <TotalRowCellContainer borderLeft>
                                                <CustomTypography text={formatNumberWithCommas(totals.yesterday)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                <CustomTypography text="Yesterday" sx={{ fontSize: "11px", color: "#999" }} />
                                            </TotalRowCellContainer>

                                            <TotalRowCellContainer>
                                                <TodayCountContainer>
                                                    <CustomTypography text={formatNumberWithCommas(totals.userCount)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                </TodayCountContainer>
                                                <CustomTypography text="Today" sx={{ fontSize: "11px", color: "#999" }} />
                                            </TotalRowCellContainer>

                                            <TotalRowCellContainer>
                                                <CustomTypography text={formatNumberWithCommas(totals.live)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                <CustomTypography text="Live" sx={{ fontSize: "11px", color: "#999" }} />
                                            </TotalRowCellContainer>
                                        </>
                                    )}

                                    {dateSelectionMode === "range" && (
                                        <>
                                            <TotalRowCellContainer borderLeft>
                                                <CustomTypography text={formatNumberWithCommas(totals.userCount)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                                <CustomTypography text="Users" sx={{ fontSize: "11px", color: "#999" }} />
                                            </TotalRowCellContainer>
                                            <TotalRowCellContainer>
                                                <CustomTypography text={""} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                            </TotalRowCellContainer>
                                            <TotalRowCellContainer>
                                                <CustomTypography text={""} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                            </TotalRowCellContainer>
                                        </>
                                    )}

                                    <TotalRowCellContainer borderLeft>
                                        <DataUsageContainer>
                                            <CustomTypography text={totals.download} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                            <DataUnitSpan>GB</DataUnitSpan>
                                        </DataUsageContainer>
                                        <CustomTypography text="Download" sx={{ fontSize: "11px", color: "#999" }} />
                                    </TotalRowCellContainer>

                                    <TotalRowCellContainer>
                                        <DataUsageContainer>
                                            <CustomTypography text={totals.upload} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                            <DataUnitSpan>GB</DataUnitSpan>
                                        </DataUsageContainer>
                                        <CustomTypography text="Upload" sx={{ fontSize: "11px", color: "#999" }} />
                                    </TotalRowCellContainer>
                                    <TotalRowCellContainer borderLeft>
                                        <DataUsageContainer>
                                            <CustomTypography text={formatNumberWithCommas(volteCallsCount)} sx={{ fontWeight: 700, fontSize: "18px" }} />
                                        </DataUsageContainer>
                                        <CustomTypography text="Calls" sx={{ fontSize: "11px", color: "#999" }} />
                                    </TotalRowCellContainer>
                                </TotalRow>
                            </TableInnerWrapper>
                        </TableWrapper>
                    </>
                );
        }
    };

    return (
        <TableContainer>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", sm: "flex-start", md: "space-between" },
                    marginBottom: "24px",
                    gap: "16px",
                }}
            >
                <Box sx={{
                    display: { xs: "none", sm: "none", md: "flex" },
                    gap: "16px",
                }}></Box>
                <Box sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
                    <UniversityImage
                        src={getImagePath(universityName)}
                        alt={`Image for ${universityName}`}
                    />
                </Box>
                <Stack direction={"row"}>
                    {/* Integrated buttons with functionality */}
                    <Box sx={{ display: "flex", gap: "4px", justifyContent: "space-between", alignItems: "center", marginTop: { xs: "1rem", sm: "1rem", md: 0 } }}>
                        {dateSelectionMode === "range" && <>
                            <ButtonGroup
                                variant="outlined"
                                ref={anchorRef}
                                aria-label="View options"
                                sx={{
                                    '& .MuiButton-root': {
                                        borderColor: "#0000001F",
                                        color: "#005AA6",
                                        '&:hover': {
                                            borderColor: "#0a2240",
                                            backgroundColor: "rgba(10, 34, 64, 0.04)",
                                        }
                                    }
                                }}

                            >
                                <Button
                                    // onClick={handleDropdownClick}
                                    onClick={handleDropdownToggle}
                                    sx={{
                                        height: "2rem",
                                        textTransform: 'none',
                                        fontSize: '14px',
                                        ...(downloadStatus === 'downloading' && {
                                            opacity: 0.7,
                                            cursor: 'not-allowed'
                                        })
                                    }}
                                    disabled={downloadStatus === 'downloading'}
                                >
                                    {viewOptions[activeSelectedIndex].icon}
                                    {viewOptions[activeSelectedIndex].label}
                                </Button>
                                <Button
                                    size="small"
                                    aria-controls={dropdownOpen ? 'view-options-menu' : undefined}
                                    aria-expanded={dropdownOpen ? 'true' : undefined}
                                    aria-label="select view option"
                                    aria-haspopup="menu"
                                    onClick={handleDropdownToggle}
                                    sx={{
                                        minWidth: "32px",
                                        height: "2rem",
                                        padding: 0,
                                    }}
                                >
                                    <ArrowDropDownIcon />
                                </Button>
                            </ButtonGroup>
                            <Popper
                                sx={{ zIndex: 1 }}
                                open={dropdownOpen}
                                anchorEl={anchorRef.current}
                                role={undefined}
                                transition
                                disablePortal
                            >
                                {({ TransitionProps, placement }) => (
                                    <Grow
                                        {...TransitionProps}
                                        style={{
                                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                                        }}
                                    >
                                        <Paper>
                                            <ClickAwayListener onClickAway={handleDropdownClose}>
                                                <MenuList id="view-options-menu" autoFocusItem>
                                                    {viewOptions.map((option, index) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            selected={index === activeSelectedIndex}
                                                            onClick={(event) => handleMenuItemClick(event, index)}
                                                            disabled={option.value === 'download' && downloadStatus === 'downloading'}
                                                        >
                                                            {option.icon}
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Grow>
                                )}
                            </Popper>
                            <Divider orientation="vertical" flexItem sx={{ marginLeft: "8px", marginRight: "5px" }} />
                        </>
                        }


                        {perfValue && (
                            <Chip
                                label={
                                    perfValue !== ""
                                        ? `Availability - ${perfValue}%`
                                        : "--"
                                }
                                size="medium"
                                sx={{
                                    bgcolor: formatChipBgColorAvaliability(perfValue),
                                    color: formatChipFontColorAvaliability(perfValue),
                                    fontWeight: 600,
                                    fontSize: "15px",
                                    minWidth: 62,
                                    justifyContent: "center",
                                }}
                            />
                        )}
                    </Box>


                </Stack>
            </Box>

            {renderView()}
            {/* Render current view */}
        </TableContainer>
    );
};
